import { apiRequest } from '../apiRequest';

export const organisationsIntegrationsAPI = {
  updateGitHubIntegration: async (
    orgId,
    { githubToken, githubOrgLogin }: { githubToken?: string; githubOrgLogin?: string },
  ) => {
    const body: { githubToken?: string; githubOrgLogin?: string } = {};
    if (githubToken !== undefined) body.githubToken = githubToken;
    if (githubOrgLogin !== undefined) body.githubOrgLogin = githubOrgLogin;
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-integration`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return data.organisation ?? data;
  },
  testGitHubIntegration: async (
    orgId,
    credentials: { githubToken?: string; githubOrgLogin?: string } | null = null,
  ) => {
    const body =
      credentials && (credentials.githubToken != null || credentials.githubOrgLogin != null)
        ? JSON.stringify({
            ...(credentials.githubToken != null ? { githubToken: credentials.githubToken } : {}),
            ...(credentials.githubOrgLogin != null
              ? { githubOrgLogin: credentials.githubOrgLogin }
              : {}),
          })
        : undefined;
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-integration/test`, {
      method: 'POST',
      ...(body ? { body } : {}),
    });
    return data;
  },
  listGithubTokens: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-tokens`, { method: 'GET' });
    return data.tokens ?? [];
  },
  addGithubToken: async (orgId, { label, token }) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-tokens`, {
      method: 'POST',
      body: JSON.stringify({ label, token }),
    });
    return data.tokens ?? [];
  },
  removeGithubToken: async (orgId, tokenId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-tokens/${tokenId}`, {
      method: 'DELETE',
    });
    return data.tokens ?? [];
  },
  testGithubToken: async (orgId, tokenId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/github-tokens/${tokenId}/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return data;
  },
  getGithubWebhookDeliveries: async (orgId, options: any = {}) => {
    const params = new URLSearchParams();
    if (options.limit != null) params.set('limit', String(options.limit));
    if (options.offset != null) params.set('offset', String(options.offset));
    if (options.status != null && String(options.status).trim() !== '')
      params.set('status', String(options.status).trim());
    if (options.repositoryGitHubId != null)
      params.set('repositoryGitHubId', String(options.repositoryGitHubId));
    if (options.includePayload) params.set('includePayload', '1');
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/github-webhook-deliveries${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return {
      deliveries: data.deliveries ?? [],
      total: data.total ?? 0,
      limit: data.limit ?? 50,
      offset: data.offset ?? 0,
    };
  },
  getSyncMonitoringSummary: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/sync-monitoring/summary`, {
      method: 'GET',
    });
    return {
      counts: data.counts ?? { total: 0, syncing: 0, failing: 0, incomplete: 0, neverSynced: 0 },
      attentionRepos: data.attentionRepos ?? [],
      prEnrichmentBacklog: data.prEnrichmentBacklog ?? 0,
      scheduler: data.scheduler ?? {
        lastRunAt: null,
        lastRunStats: null,
        queueDepth: 0,
        paused: false,
        pausedAt: null,
      },
    };
  },
  getSyncMonitoringTokens: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/sync-monitoring/tokens`, {
      method: 'GET',
    });
    return data.tokens ?? [];
  },
  getSyncMonitoringTokenLogs: async (
    orgId,
    tokenId,
    { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {}
  ) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/sync-monitoring/tokens/${tokenId}/logs?${params.toString()}`,
      { method: 'GET' }
    );
    return {
      token: data.token ?? null,
      logs: data.logs ?? [],
      total: data.total ?? 0,
      limit: data.limit ?? limit,
      offset: data.offset ?? offset,
    };
  },
  getSyncMonitoringRepositories: async (orgId, scope: 'connected' | 'discovered' | 'all' = 'connected') => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/sync-monitoring/repositories?scope=${scope}`,
      { method: 'GET' }
    );
    return data.repositories ?? [];
  },
  cancelAllSyncing: async (orgId) => {
    return await apiRequest<any>(`/api/organisations/${orgId}/sync-monitoring/cancel-all`, {
      method: 'POST',
    });
  },
  pauseAutoSync: async (orgId) => {
    return await apiRequest<any>(`/api/organisations/${orgId}/sync-monitoring/scheduler/pause`, {
      method: 'POST',
    });
  },
  resumeAutoSync: async (orgId) => {
    return await apiRequest<any>(`/api/organisations/${orgId}/sync-monitoring/scheduler/resume`, {
      method: 'POST',
    });
  },
  /**
   * Portfolios: all projects with connected repos, code-index status, and briefing summary
   * (requires api.organisations.portfolio.read).
   * Each project includes `briefing`: generationStatus, hasContent, canGenerate, wikiStale, etc.
   */ listAiProviders: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/ai-providers`, { method: 'GET' });
    return data.providers ?? [];
  },
  upsertAiProvider: async (orgId, provider, { apiKey, label }) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/ai-providers/${provider}`, {
      method: 'PUT',
      body: JSON.stringify({ apiKey, label }),
    });
    return data.provider;
  },
  removeAiProvider: async (orgId, provider) => {
    await apiRequest<any>(`/api/organisations/${orgId}/ai-providers/${provider}`, { method: 'DELETE' });
  },
  getAiConfig: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/ai-config`, { method: 'GET' });
    return data.config ?? null;
  },
  setAiConfig: async (orgId, { defaultProvider, defaultModel }) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/ai-config`, {
      method: 'PUT',
      body: JSON.stringify({ defaultProvider, defaultModel }),
    });
    return data.config;
  },
  getAiUsage: async (orgId, { days = 30, feature }: { days?: number; feature?: string } = {}) => {
    const params = new URLSearchParams();
    if (days != null) params.set('days', String(days));
    if (feature) params.set('feature', feature);
    const qs = params.toString();
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/ai/usage${qs ? `?${qs}` : ''}`,
      { method: 'GET' }
    );
    return data;
  },
  getEmailConfig: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/email-config`, { method: 'GET' });
    return data;
  },
  sendEmailConfigTest: async (orgId) => {
    const data = await apiRequest<any>(`/api/organisations/${orgId}/email-config/test`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return data;
  },
  getSuggestionNotificationRecipients: async (orgId) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/suggestion-notification-recipients`,
      {
        method: 'GET',
      }
    );
    return data;
  },
  putSuggestionNotificationRecipients: async (orgId, recipients) => {
    const data = await apiRequest<any>(
      `/api/organisations/${orgId}/suggestion-notification-recipients`,
      {
        method: 'PUT',
        body: JSON.stringify({ recipients }),
      }
    );
    return data;
  },
};
